import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const TradeFilters = ({ onApplyFilters, onResetFilters, isVisible, onToggle }) => {
  const [filters, setFilters] = useState({
    symbol: '',
    assetType: '',
    status: '',
    strategy: '',
    dateRange: 'today',
    minAmount: '',
    maxAmount: '',
    aiConfidenceMin: '',
    executionLatencyMax: ''
  });

  const [savedFilters, setSavedFilters] = useState([
    { id: 1, name: 'High Volume Trades', active: false },
    { id: 2, name: 'Failed Executions', active: false },
    { id: 3, name: 'Crypto Only', active: false },
    { id: 4, name: 'Low Confidence AI', active: false }
  ]);

  const assetTypeOptions = [
    { value: '', label: 'All Asset Types' },
    { value: 'stock', label: 'Indian Stocks' },
    { value: 'crypto', label: 'Cryptocurrency' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'executed', label: 'Executed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const strategyOptions = [
    { value: '', label: 'All Strategies' },
    { value: 'momentum', label: 'Momentum Trading' },
    { value: 'arbitrage', label: 'Arbitrage' },
    { value: 'mean_reversion', label: 'Mean Reversion' },
    { value: 'scalping', label: 'Scalping' },
    { value: 'swing', label: 'Swing Trading' }
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      symbol: '',
      assetType: '',
      status: '',
      strategy: '',
      dateRange: 'today',
      minAmount: '',
      maxAmount: '',
      aiConfidenceMin: '',
      executionLatencyMax: ''
    };
    setFilters(resetFilters);
    onResetFilters();
  };

  const handleSavedFilterToggle = (filterId) => {
    setSavedFilters(prev => prev?.map(filter => 
      filter?.id === filterId 
        ? { ...filter, active: !filter?.active }
        : { ...filter, active: false }
    ));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 bg-card border-l border-border z-40 overflow-y-auto">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Trade Filters</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            iconName="X"
            iconSize={16}
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Saved Filter Presets */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Quick Filters</h4>
          <div className="space-y-2">
            {savedFilters?.map((filter) => (
              <div key={filter?.id} className="flex items-center space-x-2">
                <Checkbox
                  checked={filter?.active}
                  onChange={() => handleSavedFilterToggle(filter?.id)}
                  size="sm"
                />
                <span className="text-sm text-muted-foreground">{filter?.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="space-y-4">
          {/* Symbol Search */}
          <Input
            label="Symbol"
            type="text"
            placeholder="Search by symbol (e.g., RELIANCE, BTC)"
            value={filters?.symbol}
            onChange={(e) => handleFilterChange('symbol', e?.target?.value)}
          />

          {/* Asset Type */}
          <Select
            label="Asset Type"
            options={assetTypeOptions}
            value={filters?.assetType}
            onChange={(value) => handleFilterChange('assetType', value)}
          />

          {/* Status */}
          <Select
            label="Execution Status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />

          {/* Strategy */}
          <Select
            label="Trading Strategy"
            options={strategyOptions}
            value={filters?.strategy}
            onChange={(value) => handleFilterChange('strategy', value)}
          />

          {/* Date Range */}
          <Select
            label="Date Range"
            options={dateRangeOptions}
            value={filters?.dateRange}
            onChange={(value) => handleFilterChange('dateRange', value)}
          />

          {/* Amount Range */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Min Amount (₹)"
              type="number"
              placeholder="0"
              value={filters?.minAmount}
              onChange={(e) => handleFilterChange('minAmount', e?.target?.value)}
            />
            <Input
              label="Max Amount (₹)"
              type="number"
              placeholder="1000000"
              value={filters?.maxAmount}
              onChange={(e) => handleFilterChange('maxAmount', e?.target?.value)}
            />
          </div>

          {/* AI Confidence */}
          <Input
            label="Min AI Confidence (%)"
            type="number"
            placeholder="0"
            min="0"
            max="100"
            value={filters?.aiConfidenceMin}
            onChange={(e) => handleFilterChange('aiConfidenceMin', e?.target?.value)}
          />

          {/* Execution Latency */}
          <Input
            label="Max Execution Latency (ms)"
            type="number"
            placeholder="1000"
            value={filters?.executionLatencyMax}
            onChange={(e) => handleFilterChange('executionLatencyMax', e?.target?.value)}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleResetFilters}
            className="flex-1"
          >
            Reset
          </Button>
          <Button
            variant="default"
            onClick={handleApplyFilters}
            className="flex-1"
          >
            Apply Filters
          </Button>
        </div>

        {/* Active Filters Count */}
        <div className="mt-4 p-3 bg-muted rounded-md">
          <div className="flex items-center space-x-2">
            <Icon name="Info" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {Object.values(filters)?.filter(value => value !== '')?.length} active filters
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeFilters;